import { useEffect, useState } from 'react';

type UseElementOptions = {
  observe?: boolean;
  trackVisibility?: boolean;
};

const useElement = (selector: string, options: UseElementOptions = {}) => {
  const { observe = false, trackVisibility = false } = options;
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!observe) {
      setElement(document.querySelector(selector) as HTMLElement | null);
      return;
    }

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        setElement(el as HTMLElement);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [selector, observe]);

  useEffect(() => {
    if (!(trackVisibility && element)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [element, trackVisibility]);

  return { element, isVisible };
};

export default useElement;
